import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadFromLocalStorage, saveToLocalStorage } from 'src/services/localStorage';

const STORAGE_PREFIX = 'columnVisibility:';

// Resolved vs column list keeps table order and drops keys the table no longer has
const resolveVisibleColumns = (columns, defaultVisibleColumns, stored) =>
  columns
    .filter(column =>
      typeof stored?.[column.key] === 'boolean'
        ? stored[column.key]
        : defaultVisibleColumns.includes(column.key),
    )
    .map(column => column.key);

// Persists a table's chosen columns to local storage, keyed by table
export const useColumnVisibility = (tableKey, columns, defaultVisibleColumns) => {
  const [visibleColumns, setVisibleColumns] = useState(() =>
    resolveVisibleColumns(
      columns,
      defaultVisibleColumns,
      loadFromLocalStorage(`${STORAGE_PREFIX}${tableKey}`),
    ),
  );

  useEffect(() => {
    // Stored as a key -> bool map so an unknown key is distinguishable from a hidden one
    const stored = Object.fromEntries(
      columns.map(column => [column.key, visibleColumns.includes(column.key)]),
    );
    saveToLocalStorage(`${STORAGE_PREFIX}${tableKey}`, stored);
  }, [tableKey, columns, visibleColumns]);

  const isColumnVisible = useCallback(
    column => visibleColumns.includes(column.key),
    [visibleColumns],
  );

  const setColumnVisible = useCallback(
    (columnKey, isVisible) =>
      setVisibleColumns(prev =>
        columns
          .filter(column => (column.key === columnKey ? isVisible : prev.includes(column.key)))
          .map(column => column.key),
      ),
    [columns],
  );

  const resetColumns = useCallback(
    () => setVisibleColumns(resolveVisibleColumns(columns, defaultVisibleColumns)),
    [columns, defaultVisibleColumns],
  );

  const visibleColumnDefinitions = useMemo(
    () => columns.filter(column => visibleColumns.includes(column.key)),
    [columns, visibleColumns],
  );

  return { visibleColumnDefinitions, isColumnVisible, setColumnVisible, resetColumns };
};
